import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom'
import Layout from './components/Layout'
import IntroVeil from './components/IntroVeil'
import { useEffect, lazy, Suspense } from 'react'
import { trackPageView } from './lib/analytics'

// ScrollToTop as inline component to ensure it's inside Routes. Also reports the
// SPA route change to Google Analytics (GA4 only auto-tracks the first load).
function ScrollToTop() {
  const { pathname, hash } = useLocation()
  useEffect(() => {
    // ponytail: a #hash target may not be mounted on the first frame (lazy
    // route / data-driven section) — one rAF is enough in practice.
    if (hash) {
      requestAnimationFrame(() => {
        document
          .querySelector(hash)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    } else {
      window.scrollTo(0, 0)
    }
    trackPageView(pathname, document.title)
  }, [pathname, hash])
  return null
}

// Root layout that includes ScrollToTop
function RootLayout() {
  return (
    <>
      <IntroVeil />
      <ScrollToTop />
      <Outlet />
    </>
  )
}
import Home from './pages/Home'
import { useDataPrefetch } from './hooks/useDataPrefetch'
// Public pages are lazy-loaded so the initial bundle ships only the shell + Home
// (the landing page, kept eager). Each other route becomes its own chunk, fetched
// on navigation — this is what keeps the entry bundle small. The <main> in Layout
// provides the Suspense boundary, so the header/footer stay visible while a page
// chunk loads. Admin routes below are lazy for the same reason.
const Tours = lazy(() => import('./pages/Tours'))
const TourDetail = lazy(() => import('./pages/TourDetail'))
const Treks = lazy(() => import('./pages/Treks'))
const TrekDetail = lazy(() => import('./pages/TrekDetail'))
const RockClimbing = lazy(() => import('./pages/RockClimbing'))
const RockClimbingDetail = lazy(() => import('./pages/RockClimbingDetail'))
const ElectricEnduroVangVieng = lazy(
  () => import('./pages/ElectricEnduroVangVieng')
)
const SelfGuidedRoutes = lazy(() => import('./pages/SelfGuidedRoutes'))
const ActivityDetail = lazy(() => import('./pages/ActivityDetail'))
const About = lazy(() => import('./pages/About'))
const FounderBio = lazy(() => import('./pages/FounderBio'))
const FAQ = lazy(() => import('./pages/FAQ'))
const Contact = lazy(() => import('./pages/Contact'))
const Blog = lazy(() => import('./pages/Blog'))
const BlogPost = lazy(() => import('./pages/BlogPost'))
const Chatbot = lazy(() => import('./pages/Chatbot'))
const Partners = lazy(() => import('./pages/Partners'))
import { AdminProvider } from './contexts/AdminContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { CartProvider } from './contexts/CartContext'
import { ConnectivityBanner } from './components/ConnectivityBanner'
import ProtectedRoute from './components/ProtectedRoute'

// Admin bundle: lazy-load — never shipped to public visitors.
// Drops ~1MB+ off the initial bundle for users browsing the public site.
const AdminLogin = lazy(() => import('./features/admin/AdminLogin'))
const AdminLayout = lazy(() => import('./features/admin/AdminLayout'))
const AdminDashboard = lazy(() => import('./features/admin/AdminDashboard'))
const ToursManager = lazy(() => import('./features/admin/ToursManager'))
const TreksManager = lazy(() => import('./features/admin/TreksManager'))
const GuidesManager = lazy(() => import('./features/admin/GuidesManager'))
const LeadsManager = lazy(() => import('./features/admin/LeadsManager'))
const EquipmentManager = lazy(() => import('./features/admin/EquipmentManager'))
const AdminSiteSettings = lazy(
  () => import('./features/admin/AdminSiteSettings')
)
const TourManagement = lazy(() => import('./features/admin/TourManagement'))
const UserManagement = lazy(() => import('./features/admin/UserManagement'))
const ChangePassword = lazy(() => import('./features/admin/ChangePassword'))
const Analytics = lazy(() => import('./features/admin/Analytics'))
const RentalsManager = lazy(() => import('./features/admin/RentalsManager'))
const RentalBookingsManager = lazy(
  () => import('./features/admin/RentalBookingsManager')
)
const MaintenanceManager = lazy(
  () => import('./features/admin/MaintenanceManager')
)
const ActivitiesManager = lazy(
  () => import('./features/admin/ActivitiesManager')
)
const SelfGuidedRoutesManager = lazy(
  () => import('./features/admin/SelfGuidedRoutesManager')
)
const GoogleCalendarSync = lazy(
  () => import('./features/admin/GoogleCalendarSync')
)
const GoogleOAuthSettings = lazy(() =>
  import('./features/admin/GoogleOAuthSettings').then((m) => ({
    default: m.GoogleOAuthSettings,
  }))
)
const FAQManager = lazy(() => import('./features/admin/FAQManager'))
const PartnersManager = lazy(() => import('./features/admin/PartnersManager'))
const BlogList = lazy(() => import('./pages/admin/BlogList'))
const BlogEditor = lazy(() => import('./pages/admin/BlogEditor'))

// Lightweight loader for Suspense fallback
const AdminLoader = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
    }}
  >
    <div
      style={{
        width: 40,
        height: 40,
        border: '3px solid #c45a1a',
        borderTopColor: 'transparent',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }}
    />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
)

function App() {
  // Prefetch critical data on app initialization for faster page loads
  useDataPrefetch()

  return (
    <ThemeProvider>
      <AdminProvider>
        <CartProvider>
          <ConnectivityBanner />
          <Routes>
            {/* Root layout wrapper for ScrollToTop */}
            <Route element={<RootLayout />}>
              {/* Public Routes */}
              <Route
                path="/"
                element={
                  <Layout>
                    <Home />
                  </Layout>
                }
              />
              <Route
                path="/tours"
                element={
                  <Layout>
                    <Tours />
                  </Layout>
                }
              />
              <Route
                path="/tours/:slug"
                element={
                  <Layout>
                    <TourDetail />
                  </Layout>
                }
              />
              {/* Singular /tour/:slug matches meta-server SSR canonical URLs */}
              <Route
                path="/tour/:slug"
                element={
                  <Layout>
                    <TourDetail />
                  </Layout>
                }
              />
              <Route
                path="/trek"
                element={
                  <Layout>
                    <Treks />
                  </Layout>
                }
              />
              <Route
                path="/trek/:slug"
                element={
                  <Layout>
                    <TrekDetail />
                  </Layout>
                }
              />
              <Route
                path="/rock-climbing"
                element={
                  <Layout>
                    <RockClimbing />
                  </Layout>
                }
              />
              <Route
                path="/rock-climbing/:slug"
                element={
                  <Layout>
                    <RockClimbingDetail />
                  </Layout>
                }
              />
              {/* Retired page — every activity now lives in the home page's
                  activities section. Kept as a redirect so the indexed URL and
                  old inbound links don't 404. */}
              <Route
                path="/beyond-enduro"
                element={<Navigate to="/#other-adventures" replace />}
              />
              <Route
                path="/self-guided-routes"
                element={
                  <Layout>
                    <SelfGuidedRoutes />
                  </Layout>
                }
              />
              <Route
                path="/paramotor-vang-vieng"
                element={
                  <Layout>
                    <ActivityDetail overrideSlug="paramotor-tandem-flight" />
                  </Layout>
                }
              />
              {/* Pillar landing page — keyword-rich URL for "electric enduro Vang Vieng" */}
              <Route
                path="/electric-enduro-vang-vieng"
                element={
                  <Layout>
                    <ElectricEnduroVangVieng />
                  </Layout>
                }
              />
              <Route
                path="/activities/:slug"
                element={
                  <Layout>
                    <ActivityDetail />
                  </Layout>
                }
              />
              {/* Singular /activity/:slug matches meta-server SSR canonical URLs */}
              <Route
                path="/activity/:slug"
                element={
                  <Layout>
                    <ActivityDetail />
                  </Layout>
                }
              />
              <Route
                path="/guides"
                element={<Navigate to="/about#guides" replace />}
              />
              <Route
                path="/about"
                element={
                  <Layout>
                    <About />
                  </Layout>
                }
              />
              {/* Founder bio pages — match meta-server SSR routes so humans see
                  the same content crawlers do (instead of being bounced to / by the catch-all). */}
              <Route
                path="/about/:founder"
                element={
                  <Layout>
                    <FounderBio />
                  </Layout>
                }
              />
              <Route
                path="/partners"
                element={
                  <Layout>
                    <Suspense
                      fallback={<div className="min-h-screen bg-garage-dark" />}
                    >
                      <Partners />
                    </Suspense>
                  </Layout>
                }
              />
              <Route
                path="/faq"
                element={
                  <Layout>
                    <FAQ />
                  </Layout>
                }
              />
              <Route
                path="/contact"
                element={
                  <Layout>
                    <Contact />
                  </Layout>
                }
              />
              <Route
                path="/blog"
                element={
                  <Layout>
                    <Blog />
                  </Layout>
                }
              />
              <Route
                path="/blog/:slug"
                element={
                  <Layout>
                    <BlogPost />
                  </Layout>
                }
              />
              <Route
                path="/chatbot"
                element={
                  <Layout>
                    <Chatbot />
                  </Layout>
                }
              />

              {/* Admin Routes — wrapped in Suspense to support lazy-loaded chunks */}
              <Route
                path="/admin/login"
                element={
                  <Suspense fallback={<AdminLoader />}>
                    <AdminLogin />
                  </Suspense>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<AdminLoader />}>
                      <AdminLayout />
                    </Suspense>
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="calendar" element={<TourManagement />} />
                <Route path="tours" element={<ToursManager />} />
                <Route path="trek" element={<TreksManager />} />
                <Route
                  path="self-guided-routes"
                  element={<SelfGuidedRoutesManager />}
                />
                <Route path="guides" element={<GuidesManager />} />
                <Route path="leads" element={<LeadsManager />} />
                <Route path="equipment" element={<EquipmentManager />} />
                <Route path="activities" element={<ActivitiesManager />} />
                <Route path="rentals" element={<RentalsManager />} />
                <Route
                  path="rental-bookings"
                  element={<RentalBookingsManager />}
                />
                <Route path="maintenance" element={<MaintenanceManager />} />
                <Route path="blog" element={<BlogList />} />
                <Route path="blog/new" element={<BlogEditor />} />
                <Route path="blog/:id" element={<BlogEditor />} />
                <Route path="settings" element={<AdminSiteSettings />} />
                <Route
                  path="google-calendar"
                  element={<GoogleCalendarSync />}
                />
                <Route path="google-oauth" element={<GoogleOAuthSettings />} />
                <Route path="faq" element={<FAQManager />} />
                <Route path="partners" element={<PartnersManager />} />
                <Route path="users" element={<UserManagement />} />
                <Route path="change-password" element={<ChangePassword />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </CartProvider>
      </AdminProvider>
    </ThemeProvider>
  )
}

export default App
